import { useDebounce } from 'ahooks'
import { useCallback, useMemo, useState } from 'react'
import { useCompanyWorkers } from 'queries/company'
import { Select, SelectProps } from 'ui'
import { ActionMeta, OnChangeValue, SingleValue } from 'react-select'
import { SelectableOption } from 'typings/selectable_ext'

type Props = Omit<SelectProps, 'value'> & {
  value?: number
}

export function WorkerSelector(props: Props) {
  const {
    value: selectedWorkerId,
    onChange: onChangeProp,
    ...otherProps
  } = props
  const [workerSearch, setWorkerSearch] = useState('')
  const debouncedWorkerSearch = useDebounce(workerSearch, { wait: 400 })
  const { data: workerResults, isFetching } = useCompanyWorkers({
    search: debouncedWorkerSearch,
  })

  const options = useMemo(() => {
    return (
      workerResults?.pages
        .flatMap((page) => page.items)
        .map((worker) => ({
          value: Number(worker.id),
          label: worker.name,
        })) ?? []
    )
  }, [workerResults])

  const value = useMemo(() => {
    if (selectedWorkerId == null) {
      return null
    }

    return (
      options.find((option) => option.value === selectedWorkerId) ?? {
        value: selectedWorkerId,
        label: `Worker #${selectedWorkerId}`,
      }
    )
  }, [selectedWorkerId, options])

  const onChange = useCallback(
    (
      value: OnChangeValue<{ value: unknown; label: string }, false>,
      actionMeta: ActionMeta<{ value: unknown; label: string }>
    ) => {
      if (value === null) {
        setWorkerSearch('')
      }

      onChangeProp?.(value, actionMeta)
    },
    [onChangeProp, setWorkerSearch]
  )

  return (
    <Select
      aria-label="Worker Selector"
      options={options}
      {...otherProps}
      isMulti={false}
      isSearchable
      isLoading={isFetching}
      filterOption={null}
      value={value}
      onChange={onChange}
      noOptionsMessage={() =>
        debouncedWorkerSearch.length < 3
          ? 'Type at least 3 characters'
          : 'No workers found'
      }
      onInputChange={(inputValue, { action }) => {
        if (action === 'input-change') {
          setWorkerSearch(inputValue)
        }
      }}
    />
  )
}
